import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Partner {
  id: string;
  name: string;
  logo_url: string;
  redirect_url: string | null;
  order_index: number;
  is_active: boolean;
  logo_size: number;
  created_at: string;
  updated_at: string;
}

export interface PartnerFormData {
  name: string;
  logo_url: string;
  redirect_url?: string | null;
  order_index?: number;
  is_active?: boolean;
  logo_size?: number;
}

// Hook pour récupérer les partenaires actifs (front-end)
export const useActivePartners = () => {
  return useQuery({
    queryKey: ['partners', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data as Partner[];
    },
  });
};

// Hook pour récupérer tous les partenaires (admin)
export const useAllPartners = () => {
  return useQuery({
    queryKey: ['partners', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data as Partner[];
    },
  });
};

// Hook pour créer un partenaire
export const useCreatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (partnerData: PartnerFormData) => {
      // Récupérer le plus grand order_index
      const { data: maxOrderData } = await supabase
        .from('partners')
        .select('order_index')
        .order('order_index', { ascending: false })
        .limit(1)
        .single();

      const nextOrderIndex = (maxOrderData?.order_index || 0) + 1;

      const { data, error } = await supabase
        .from('partners')
        .insert({
          ...partnerData,
          order_index: partnerData.order_index ?? nextOrderIndex,
          is_active: partnerData.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partenaire ajouté avec succès');
    },
    onError: (error) => {
      toast.error(`Erreur lors de l'ajout: ${error.message}`);
    },
  });
};

// Hook pour mettre à jour un partenaire
export const useUpdatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...partnerData }: Partial<Partner> & { id: string }) => {
      const { data, error } = await supabase
        .from('partners')
        .update(partnerData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partenaire mis à jour');
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`);
    },
  });
};

// Hook pour supprimer un partenaire
export const useDeletePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('partners')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partenaire supprimé');
    },
    onError: (error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    },
  });
};

// Hook pour réordonner les partenaires
export const useReorderPartners = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (partners: { id: string; order_index: number }[]) => {
      // Mise à jour en batch
      const promises = partners.map(({ id, order_index }) =>
        supabase
          .from('partners')
          .update({ order_index })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter((r) => r.error);
      
      if (errors.length > 0) {
        throw new Error('Erreur lors de la réorganisation');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Ordre mis à jour');
    },
    onError: (error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });
};

// Hook pour basculer le statut actif
export const useTogglePartnerActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('partners')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(data.is_active ? 'Partenaire activé' : 'Partenaire désactivé');
    },
    onError: (error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });
};
